import { IonIcon } from '@ionic/react';
import { chevronBack, chevronForward } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IState } from 'appRedux/createStore';
import { useDynamicCSS } from 'hooks/useDynamicCSS';
import useWhiteLabelStyles, { WHITE_LABEL_TYPE_ENUM } from 'hooks/useWhiteLabelStyles';
import { ICasinoReport, IHistorySlotInfo } from 'appRedux/models/casinoModels';
import { SlotHistoryReduxCommandCreator } from 'appRedux/actions/slotHistoryCommandCreator';
import { selectCasinoId } from 'appRedux/selectors';
import useFavorites from 'common/reactHooks/useFavorites';

import './machine-name-header.scss';
import { IUserFavorite } from 'appRedux/models/playerModels';
import { LikeButton } from 'pages/Favorites/components/LikeButton';
import { getCleanGameName } from 'common/common';
import TextMarquee from 'components/TextMarquee';
import {
  egmDetailKey,
  insightCardName,
  insightLabel,
  isInsightCardRow,
} from 'services/insightCasino';

interface MachineNavProps {
  selectedSlotData: ICasinoReport;
}
const MachineNameHeader: React.FC<MachineNavProps> = ({ selectedSlotData }: MachineNavProps) => {
  const dispatch = useDispatch();
  const historyCommands = SlotHistoryReduxCommandCreator(dispatch);

  const slotsNavRef = useRef(null);
  const [showSearchField, setShowSearchField] = useState(false);

  const [isFavoriteSlot, setIsFavoriteSlot] = useState(false);

  const { slotNumbers, activeSlotNumber } = useSelector<IState>(
    (state) => state.app.persistedState.historyState
  ) as IHistorySlotInfo;

  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;
  // Favorites are scoped by the casino business id (casinoSchema.casinoId),
  // matching playlist — distinct from kpCasinoPk used for theming above.
  const favoriteCasinoId = useSelector(selectCasinoId);

  const {
    handleInsertFavorite,
    handleDeleteFavorite,
    fetchUserFavorites,
    fetchSlotFavorites,
    slotFavoriteCount,
  } = useFavorites({});

  const userId = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.appUserPk
  ) as string;
  const favorites = useSelector<IState>(
    (state) => state.app.persistedState.favorites.favoritesList
  ) as IUserFavorite[];

  const styles = useWhiteLabelStyles(WHITE_LABEL_TYPE_ENUM.CANVAS);
  const btnstyles = useWhiteLabelStyles(WHITE_LABEL_TYPE_ENUM.BUTTON);

  const css = `
  .header-machine .machine-nav .machine-nav-likes .like-btn {
        filter : ${casinoId === 1 ? 'brightness(0) invert(1)' : 'brightness(1) invert(1)'}
    }
  .header-machine .machine-nav .machine-nav-likes .like-active {
    filter: unset
   }
  `;
  useDynamicCSS(css);

  useEffect(() => {
    if (userId && favoriteCasinoId) {
      fetchUserFavorites(userId, String(favoriteCasinoId));
    }
  }, [fetchUserFavorites, userId, favoriteCasinoId]);

  useEffect(() => {
    if (favorites && favorites.length > 0 && activeSlotNumber && favoriteCasinoId) {
      const isFavorite = favorites.some(
        (favorite) => favorite.slotNumber === Number(activeSlotNumber)
      );

      setIsFavoriteSlot(isFavorite);
      fetchSlotFavorites(activeSlotNumber.toString(), String(favoriteCasinoId));
    }
  }, [favorites, activeSlotNumber, favoriteCasinoId]);
  const goForward = () => {
    const currentIndex = slotNumbers.indexOf(activeSlotNumber);
    const nextIndex = (currentIndex + 1) % slotNumbers.length;
    if (nextIndex === 0 || slotNumbers.length <= 1) {
      return undefined;
    }
    return slotNumbers[nextIndex];
  };

  const goPrevious = () => {
    const currentIndex = slotNumbers.indexOf(activeSlotNumber);
    const previousIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : slotNumbers.length - 1;
    if (currentIndex === 0 || slotNumbers.length <= 1) {
      return undefined;
    }
    return slotNumbers[previousIndex];
  };

  const goToNextSlot = () => {
    const forwardNumber = goForward();
    if (forwardNumber) {
      historyCommands.UpdateActiveSlotNumber({
        activeSlotNumber: forwardNumber,
      });
    }
  };

  const goToPreviousSlot = () => {
    const previousNumber = goPrevious();
    if (previousNumber) {
      historyCommands.UpdateActiveSlotNumber({
        activeSlotNumber: previousNumber,
      });
    }
  };

  const handleFavoriteBtnClick = () => {
    if (userId && favoriteCasinoId) {
      if (!isFavoriteSlot) {
        handleInsertFavorite(String(activeSlotNumber), userId, String(favoriteCasinoId));
      } else {
        handleDeleteFavorite(String(activeSlotNumber), userId, String(favoriteCasinoId));
      }
    }
  };

  useEffect(() => {
    const observerd = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting }) => {
        if (!isIntersecting) setShowSearchField(false);
      });
    });
    observerd.observe(slotsNavRef?.current!);

    const el = document.querySelector('.machine-nav')!;
    const observer = new IntersectionObserver(
      ([e]) => e.target.classList.toggle('is-pinned', e.intersectionRatio < 1),
      { threshold: [1] }
    );
    observer.observe(el);
    return () => {
      observerd.disconnect();
      observer.disconnect();
    };
  }, []);

  return (
    <div className="header-machine">
      <div className="machine-nav" ref={slotsNavRef}>
        <div className="machine-nav-next-prev">
          <button
            onClick={goToPreviousSlot}
            className="machine-nav-next-prev-btn"
            disabled={goPrevious() === undefined}
          >
            <IonIcon
              style={goPrevious() ? btnstyles : {}}
              icon={chevronBack}
              className="machine-nav-next-prev-icon"
            />
          </button>
          <button
            onClick={goToNextSlot}
            className="machine-nav-next-prev-btn"
            disabled={goForward() === undefined}
          >
            <IonIcon
              style={goForward() ? btnstyles : {}}
              icon={chevronForward}
              className="machine-nav-next-prev-icon"
            />
          </button>
        </div>
        <div className="machine-name-header">
          <div
            className={`machine-name-header-box${isInsightCardRow(selectedSlotData) ? ' machine-name-header-box--insight' : ''}`}
          >
            <div className="machine-name-header-number machine-name-header-label">
              {isInsightCardRow(selectedSlotData) ? insightLabel(selectedSlotData) : selectedSlotData?.slot_number}
            </div>
            <div
              className="machine-name-header-name"
            >
              <TextMarquee
                text={
                  isInsightCardRow(selectedSlotData)
                    ? insightCardName(selectedSlotData)
                    : getCleanGameName(selectedSlotData?.game_name)
                }
              />
            </div>
            <div className="machine-name-header-number">{selectedSlotData?.denom}</div>
          </div>
        </div>

        <div className="machine-nav-likes">
          <span className="like-button">
            <LikeButton
              slotNumber={
                egmDetailKey(selectedSlotData) ||
                (activeSlotNumber != null ? String(activeSlotNumber) : '')
              }
              fontSize="large"
            />
          </span>
          <span style={btnstyles} className="machine-nav-likes-text">
            {slotFavoriteCount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MachineNameHeader;
